import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardHeader, EmptyState, ErrorState, Icon, PageHeader, Skeleton } from '../../../components/ui';
import { DateRange, KeyValueRow, KeyValues } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { JOB_STATUS } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODES, TXN_CATEGORIES } from '../../../constants/payments';
import { masterLabel } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { formatDate, toISODate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { reportService } from '../services/reportService';

const monthStart = () => {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
};
interface ColumnOpts {
  money?: boolean;
  total?: boolean;
  strong?: boolean;
  align?: 'left' | 'right';
  link?: (row: any) => string;
}
type ReportColumn = [header: string, value: (row: any) => any, opts?: ColumnOpts];

interface ReportDef {
  key: string;
  label: string;
  icon: string;
  /** API endpoint under /reports */
  path: string;
  note?: string;
  rows?: (data: any) => any[];
  columns?: ReportColumn[];
}

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

// Each report: API path, and table columns [header, value(row), { money, total, link }].
const REPORTS: ReportDef[] = [
  { key: 'profit-loss', label: 'Profit & loss', icon: 'chart', path: '/reports/profit-loss' },
  {
    key: 'gst-summary', label: 'GST summary', icon: 'receipt', path: '/reports/gst-summary', rows: (d) => d.rows,
    note: 'Output GST on your invoices minus input GST on purchases and expenses. Check with your CA before filing.',
    columns: [
      ['Month', (r) => monthLabel(r.month)],
      ['Sales (taxable)', (r) => r.output.taxable, { money: true, total: true }],
      ['Output GST', (r) => r.output.total, { money: true, total: true }],
      ['Purchases (taxable)', (r) => r.input.taxable, { money: true, total: true }],
      ['Input GST', (r) => r.input.total, { money: true, total: true }],
      ['Net GST payable', (r) => r.netPayable, { money: true, total: true, strong: true }],
    ],
  },
  {
    key: 'sales-register', label: 'Sales register', icon: 'receipt', path: '/reports/sales-register', rows: (d) => d.invoices,
    columns: [
      ['Invoice', (r) => r.invoiceNo, { link: (r) => `/invoices/${r._id}` }],
      ['Date', (r) => formatDate(r.date)],
      ['Customer', (r) => r.customer?.name],
      ['GSTIN', (r) => r.customer?.gstin || '-'],
      ['Taxable', (r) => r.subTotal, { money: true, total: true }],
      ['CGST', (r) => r.cgst, { money: true, total: true }],
      ['SGST', (r) => r.sgst, { money: true, total: true }],
      ['Total', (r) => r.grandTotal, { money: true, total: true, strong: true }],
      ['Balance', (r) => r.balanceAmount, { money: true, total: true }],
    ],
  },
  {
    key: 'purchase-register', label: 'Purchase register', icon: 'cart', path: '/reports/purchase-register', rows: (d) => d.purchases,
    columns: [
      ['Purchase', (r) => r.purchaseNo, { link: (r) => `/purchases/${r._id}` }],
      ['Bill no', (r) => r.vendorBillNo || '-'],
      ['Date', (r) => formatDate(r.billDate)],
      ['Supplier', (r) => r.vendor?.name],
      ['Taxable', (r) => r.subTotal, { money: true, total: true }],
      ['GST', (r) => r.totalTax, { money: true, total: true }],
      ['Total', (r) => r.grandTotal, { money: true, total: true, strong: true }],
      ['Balance', (r) => r.balanceAmount, { money: true, total: true }],
    ],
  },
  {
    key: 'job-profit', label: 'Job profit', icon: 'wrench', path: '/reports/job-profit', rows: (d) => d.rows,
    note: 'Jobs completed in the period: invoice value before GST, minus material cost and linked expenses.',
    columns: [
      ['Job', (r) => r.serviceNo, { link: (r) => `/jobs/${r._id}` }],
      ['Customer', (r) => r.customer?.name],
      ['Status', (r) => labelOf(JOB_STATUS, r.status)],
      ['Invoice', (r) => r.invoiceNo || '-'],
      ['Revenue', (r) => r.revenue, { money: true, total: true }],
      ['Material', (r) => r.materialCost, { money: true, total: true }],
      ['Expenses', (r) => r.expenses, { money: true, total: true }],
      ['Profit', (r) => r.profit, { money: true, total: true, strong: true }],
    ],
  },
  {
    key: 'expenses', label: 'Expenses by category', icon: 'moneyOut', path: '/reports/expenses-by-category', rows: (d) => d.rows,
    columns: [
      ['Category', (r) => masterLabel('expenseCategory', r.category)],
      ['Entries', (r) => r.count, { total: true, align: 'right' }],
      ['Amount', (r) => r.total, { money: true, total: true, strong: true }],
    ],
  },
  {
    key: 'daybook', label: 'Day book', icon: 'wallet', path: '/reports/daybook', rows: (d) => d.entries,
    columns: [
      ['Number', (r) => r.transactionNo, { link: (r) => `/transactions/${r._id}` }],
      ['Date', (r) => formatDate(r.date)],
      ['Party', (r) => r.partyName || '-'],
      ['Type', (r) => labelOf(TXN_CATEGORIES, r.category)],
      ['Mode', (r) => labelOf(PAYMENT_MODES, r.mode)],
      ['Money in', (r) => (r.direction === 'credit' ? r.amount : 0), { money: true, total: true }],
      ['Money out', (r) => (r.direction === 'debit' ? r.amount : 0), { money: true, total: true }],
    ],
  },
  {
    key: 'workers', label: 'Jobs by worker', icon: 'users', path: '/reports/services', rows: (d) => d.byWorker,
    columns: [
      ['Worker', (r) => r.name || 'Removed user'],
      ['Jobs completed', (r) => r.completed, { total: true, align: 'right', strong: true }],
    ],
  },
];

function toCsv(report: ReportDef, rows: any[]) {
  const cols = report.columns ?? [];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const head = cols.map(([h]) => esc(h)).join(',');
  const body = rows.map((r) => cols.map(([, get, o = {}]) => esc(o.money ? (get(r) ?? 0) / 100 : get(r))).join(','));
  const blob = new Blob([[head, ...body].join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${report.key}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

function ProfitLoss({ d }: { d: any }) {
  const exp = Object.entries((d.expenses ?? {}) as Record<string, number>).map(([k, v]): KeyValueRow => [`  ${masterLabel('expenseCategory', k)}`, money(v)]);
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2">
      <KeyValues
        rows={[
          ['Sales (before GST)', money(d.income), { strong: true }],
          ['Material used on jobs', `− ${money(d.materialCost)}`],
          ['Gross profit', money(d.grossProfit), { strong: true }],
          ['Damaged stock', `− ${money(d.damagedStock)}`],
          ['Expenses (before GST)', `− ${money(d.expenseTotal)}`],
          ...exp,
          ['Salaries and wages', `− ${money(d.wages ?? 0)}`],
        ]}
      />
      <div className={cn('flex flex-col justify-center rounded-xl p-6', d.netProfit >= 0 ? 'bg-emerald-50' : 'bg-red-50')}>
        <p className="text-sm text-ink-500">Net profit</p>
        <p className={cn('tabular mt-1 text-3xl font-semibold', d.netProfit >= 0 ? 'text-emerald-700' : 'text-red-600')}>{money(d.netProfit)}</p>
        <p className="mt-2 text-xs text-ink-500">Amounts are before GST. Wages come from salary slips in the period plus salary paid without a slip; advances count when they are recovered.</p>
      </div>
    </div>
  );
}

function ReportTable({ report, data }: { report: ReportDef; data: any }) {
  const rows = report.rows?.(data);
  if (!rows?.length) return <EmptyState icon={report.icon} title="Nothing in this period" message="Pick another date range." />;
  const cols = report.columns ?? [];
  const totals = cols.map(([, get, o = {}]) => (o.total ? rows.reduce((a, r) => a + (Number(get(r)) || 0), 0) : null));
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="border-b border-line text-[13px] text-ink-500">
          <tr>
            {cols.map(([h, , o = {}]) => (
              <th key={h} scope="col" className={cn('whitespace-nowrap px-5 py-3 font-normal', (o.money || o.align === 'right') && 'text-right')}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="tabular divide-y divide-line">
          {rows.map((r, i) => (
            <tr key={r._id ?? r.month ?? r.category ?? i}>
              {cols.map(([h, get, o = {}]) => {
                const v = get(r);
                return (
                  <td key={h} className={cn('whitespace-nowrap px-5 py-2.5', (o.money || o.align === 'right') && 'text-right', o.strong && 'font-semibold text-navy-900')}>
                    {o.link ? <Link to={o.link(r)} className="font-medium text-brand-600 hover:underline">{v}</Link> : o.money ? money(v) : v}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-line bg-navy-50/60 font-semibold text-navy-900">
          <tr>
            {cols.map(([h, , o = {}], i) => (
              <td key={h} className={cn('tabular whitespace-nowrap px-5 py-3', (o.money || o.align === 'right') && 'text-right')}>
                {i === 0 ? 'Total' : totals[i] == null ? '' : o.money ? money(totals[i]) : totals[i]}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function Reports() {
  const [active, setActive] = useState('profit-loss');
  useMasters();
  const [range, setRange] = useState({ from: monthStart(), to: toISODate(new Date()) });
  const report = REPORTS.find((r) => r.key === active) ?? REPORTS[0];
  const { data: fetched, loading, error, reload } = useFetch(
    () => reportService.get(report.path, range).then((d) => ({ key: report.key, d })),
    [report.path, range.from, range.to],
  );
  // Ignore data that belongs to the previously selected report.
  const data = fetched?.key === report.key ? fetched.d : null;

  return (
    <>
      <PageHeader title="Reports" subtitle="Numbers for the selected period" actions={<DateRange className="w-72" from={range.from} to={range.to} onChange={(patch) => setRange((r) => ({ ...r, ...patch }))} />} />
      <div className="grid items-start gap-5 lg:grid-cols-[240px_minmax(0,1fr)]">
        <Card className="p-2">
          <nav aria-label="Reports" className="flex gap-1 overflow-x-auto lg:flex-col">
            {REPORTS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setActive(r.key)}
                aria-current={active === r.key ? 'page' : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  active === r.key ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-600 hover:bg-navy-50',
                )}
              >
                <Icon name={r.icon} size={17} className={active === r.key ? 'text-brand-600' : 'text-ink-400'} />
                {r.label}
              </button>
            ))}
          </nav>
        </Card>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader
            title={report.label}
            subtitle={`${formatDate(range.from)} to ${formatDate(range.to)}${report.note ? ` · ${report.note}` : ''}`}
            action={report.columns && data && <Button size="sm" variant="outline" leftIcon="download" onClick={() => toCsv(report, report.rows?.(data) ?? [])}>CSV</Button>}
            className="border-b border-line pb-4"
          />
          {error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : loading || !data ? (
            <div className="space-y-2 p-5">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-9" />)}</div>
          ) : report.key === 'profit-loss' ? (
            <ProfitLoss d={data} />
          ) : (
            <ReportTable report={report} data={data} />
          )}
        </Card>
      </div>
    </>
  );
}
